import routingData from '../../../data/routingDashboardData.json';

/**
 * Shape of a single stop on a route.
 * `id` is the depot sentinel "DEPOT" or a numeric client id.
 */
export interface RouteStop {
	id: string | number;
	lat: number;
	lng: number;
	name: string;
}

/** A single pre-computed vehicle route. */
export interface VehicleRoute {
	vehicle: number;
	slot: string;
	trip_type: number; // 1 = Dispatch, 2 = Pickup, 3 = Dispatch & Pickup
	distance_km: number;
	duration_min: number;
	maps_link: string;
	polyline: string; // Google encoded polyline string
	stops: RouteStop[];
}

/** The depot the fleet operates out of. */
export interface Depot {
	facility_id: number;
	lat: number;
	lng: number;
	name: string;
}

/** Fleet-justification verdict for the day's plan. */
export interface RoutingJustification {
	min_autos: number;
	current_fleet: number;
	verdict: 'under_fleeted' | 'over_fleeted' | 'right_sized';
	gap: number;
	savings: number;
	justification: string;
}

/** The full routing-run payload rendered by the Route Optimizer page. */
export interface RoutingData {
	generated: string;
	city: string;
	depot: Depot;
	fleet_current: number;
	routes: VehicleRoute[];
	justification: RoutingJustification;
}

export interface UseRoutingDataResult {
	data: RoutingData | null;
	loading: boolean;
	error: Error | null;
}

/**
 * Single source of routing data for the Route Optimizer page.
 *
 * Today this reads a static, pre-computed payload bundled with the app so the
 * source can later be swapped to a live API with a one-line change here — the
 * page only consumes the {data, loading, error} contract below.
 *
 * // TODO: swap to live routing API (apiClient.get('/routing/runs/latest')) — see src/services/api.ts
 */
export const useRoutingData = (): UseRoutingDataResult => {
	const data = routingData as RoutingData;
	return { data, loading: false, error: null };
};
